import React, { useCallback, useEffect, useMemo, useState } from 'react';
import RetailerListFiltering from './RetailerListFiltering';
import RetailerListTopBar from './RetailerListTopBar';
import RetailerListStores from './RetailerListStores';
import RetailerListMap from './RetailerListMap';
import { Retailer } from '../../Types/Retailer';
import { get } from '../../Services/http';
import { triggerToast } from '../../Services/Toast.service';
import { translate } from '../../Services/translation';

interface RetailerListContainerProps {
  googleApiKey: string;
}

const RetailerListContainer = ({
  googleApiKey,
}: RetailerListContainerProps) => {
  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showMap, setShowMap] = useState(true);
  const [activeRetailer, setActiveRetailer] = useState<Retailer | null>(
    null
  );
  const [activeCountryFilter, setActiveCountryFilter] = useState<
    string | null
  >(null);
  const [activeCityFilter, setActiveCityFilter] = useState<string | null>(
    null
  );

  useEffect(() => {
    const fetchRetailers = async () => {
      setIsLoading(true);
      try {
        const res = await get('/api/retailers');
        if (res.ok) {
          setRetailers(await res.json());
        }
      } catch (err) {
        triggerToast({
          title: translate('general.error'),
          text: translate('retailers.fetch-error'),
          type: 'error',
          autoClose: false,
        });
        console.log(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRetailers();
  }, []);

  const filteredRetailerList = useMemo(() => {
    if (activeRetailer) {
      return [activeRetailer];
    } else {
      return retailers.filter(
        (r) =>
          (!activeCountryFilter ||
            r.country?.toLowerCase() ==
              activeCountryFilter.toLowerCase()) &&
          (!activeCityFilter ||
            r.city?.toLowerCase() == activeCityFilter.toLowerCase())
      );
    }
  }, [retailers, activeCityFilter, activeCountryFilter, activeRetailer]);

  const retailersAmount = useMemo(() => {
    return filteredRetailerList?.length ?? 0;
  }, [filteredRetailerList]);

  return (
    <>
      <RetailerListFiltering
        filteredRetailerList={filteredRetailerList}
        retailerList={retailers}
        setActiveCountryFilter={setActiveCountryFilter}
        setActiveCityFilter={setActiveCityFilter}
        setActiveRetailer={setActiveRetailer}
        activeCountryFilter={activeCountryFilter}
        activeCityFilter={activeCityFilter}
        activeRetailer={activeRetailer}
        isLoading={isLoading}
      />
      <RetailerListTopBar
        retailersAmount={retailersAmount}
        showMap={showMap}
        setShowMap={setShowMap}
        activeRetailer={activeRetailer}
        setActiveRetailer={setActiveRetailer}
      />
      {showMap ? (
        <RetailerListMap
          retailerList={filteredRetailerList}
          setActiveRetailer={setActiveRetailer}
          activeRetailer={activeRetailer}
          googleApiKey={googleApiKey}
          isLoading={isLoading}
        />
      ) : (
        <RetailerListStores filteredRetailerList={filteredRetailerList} />
      )}
    </>
  );
};

export default RetailerListContainer;
